import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { PageHeader, Card, Loading, EmptyState, Btn } from "@/components/primitives";
import { toast } from "sonner";
import { Building2, Plus, Pencil, Trash2, Search, Loader2, Navigation, ChevronRight, Truck } from "lucide-react";
import { mapsDir } from "@/lib/format";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const inputCls = "w-full h-10 px-3 rounded-md border border-curtron-line bg-curtron-surface text-sm outline-none focus:border-curtron-green focus:ring-2 focus:ring-curtron-green/20 transition";
const labelCls = "text-[11px] uppercase tracking-[0.05em] font-medium text-curtron-sub";
const EMPTY = { name: "", address: "", city: "", state: "", zip: "", phone: "", required_forms: [] };

function StatBox({ label, value }) {
  return (
    <div className="rounded-md border border-curtron-line p-3 text-center">
      <div className="font-heading text-xl font-bold text-curtron-ink">{value}</div>
      <div className="text-[11px] text-curtron-muted mt-0.5">{label}</div>
    </div>
  );
}

export default function Agencies() {
  const [items, setItems] = useState(null);
  const [forms, setForms] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null); // agency
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/agencies").then((r) => setItems(r.data)).catch(() => setItems([])), []);
  useEffect(() => { load(); api.get("/forms").then((r) => setForms(r.data)).catch(() => {}); }, [load]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const t = q.trim().toLowerCase();
    const list = t ? items.filter((a) => [a.name, a.city, a.state, a.address].filter(Boolean).some((v) => v.toLowerCase().includes(t))) : items;
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [items, q]);

  const groups = useMemo(() => {
    const map = {};
    filtered.forEach((a) => {
      const key = [a.city, a.state].filter(Boolean).join(", ") || "Other";
      (map[key] = map[key] || []).push(a);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (a) => { setForm({ ...EMPTY, ...a, required_forms: a.required_forms || [] }); setEditId(a.id); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleForm = (key) => setForm((f) => ({ ...f, required_forms: f.required_forms.includes(key) ? f.required_forms.filter((x) => x !== key) : [...f.required_forms, key] }));

  const openDetail = async (a) => {
    setDetail(a); setStats(null);
    try { const { data } = await api.get(`/agencies/${a.id}/stats`); setStats(data); }
    catch (e) { setStats({ totals: {}, recent: [] }); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    const payload = { name: form.name.trim(), address: form.address, city: form.city, state: form.state, zip: form.zip, phone: form.phone || null, required_forms: form.required_forms };
    try {
      if (editId) await api.patch(`/agencies/${editId}`, payload);
      else await api.post("/agencies", payload);
      toast.success(editId ? "Agency updated" : "Agency added");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/agencies/${id}`); toast.success("Agency deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (items === null) return <Loading />;

  return (
    <div>
      <PageHeader title="Agency Directory" subtitle={`${items.length} funeral home${items.length !== 1 ? "s" : ""}`}>
        <Btn onClick={openNew} data-testid="new-agency-btn"><Plus className="w-4 h-4" /> Add Agency</Btn>
      </PageHeader>

      <Card className="p-3 mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-curtron-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="agencies-search" placeholder="Search by name, city or address…"
            className="w-full h-9 pl-9 pr-3 rounded-md border border-curtron-line bg-curtron-bg text-sm outline-none focus:border-curtron-green focus:bg-curtron-surface transition-colors" />
        </div>
      </Card>

      {filtered.length === 0 ? (
        <Card><EmptyState icon={Building2} title="No agencies" action={<Btn onClick={openNew}><Plus className="w-4 h-4" /> Add Agency</Btn>} /></Card>
      ) : (
        <div className="space-y-5">
          {groups.map(([city, list]) => (
            <div key={city} data-testid={`agency-group-${city}`}>
              <h2 className="text-[11px] uppercase tracking-[0.08em] font-semibold text-curtron-muted mb-2">{city.toUpperCase()} · {list.length}</h2>
              <Card className="divide-y divide-curtron-line">
                {list.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 px-4 py-3.5" data-testid={`agency-card-${a.id}`}>
                    <span className="w-9 h-9 rounded-md bg-curtron-green/10 text-curtron-green flex items-center justify-center shrink-0"><Building2 className="w-4 h-4" /></span>
                    <button onClick={() => openDetail(a)} className="min-w-0 flex-1 text-left" data-testid={`agency-open-${a.id}`}>
                      <div className="font-medium text-curtron-ink text-sm truncate">{a.name}</div>
                      <div className="text-xs text-curtron-muted truncate">{[a.address, [a.city, a.state, a.zip].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}</div>
                    </button>
                    <div className="flex items-center gap-1 shrink-0">
                      <a href={mapsDir(a.address, a.city, a.state, a.zip)} target="_blank" rel="noopener noreferrer" className="h-8 px-2 inline-flex items-center rounded-md text-curtron-sub hover:bg-black/5" data-testid={`agency-directions-${a.id}`}><Navigation className="w-4 h-4" /></a>
                      <Btn variant="ghost" onClick={() => openEdit(a)} className="h-8 px-2" data-testid={`agency-edit-${a.id}`}><Pencil className="w-4 h-4" /></Btn>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Btn variant="ghost" className="h-8 px-2 text-curtron-terracotta" data-testid={`agency-delete-${a.id}`}><Trash2 className="w-4 h-4" /></Btn></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete {a.name}?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(a.id)} className="bg-curtron-terracotta hover:bg-[#A35549]">Delete</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      <button onClick={() => openDetail(a)} className="h-8 px-1 text-curtron-muted"><ChevronRight className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </Card>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Agency" : "Add Agency"}</DialogTitle>
            <DialogDescription>Funeral home / facility details used across transports.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><label className={labelCls}>Name *</label><input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="agency-name" className={`${inputCls} mt-1`} /></div>
            <div><label className={labelCls}>Address</label><input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="agency-address" className={`${inputCls} mt-1`} /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><label className={labelCls}>City</label><input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="agency-city" className={`${inputCls} mt-1`} /></div>
              <div><label className={labelCls}>State</label><input value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="agency-state" className={`${inputCls} mt-1`} /></div>
              <div><label className={labelCls}>ZIP</label><input value={form.zip} onChange={(e) => set("zip", e.target.value)} data-testid="agency-zip" className={`${inputCls} mt-1`} /></div>
            </div>
            <div><label className={labelCls}>Phone</label><input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="agency-phone" className={`${inputCls} mt-1`} /></div>
            {forms.length > 0 && (
              <div>
                <label className={labelCls}>Required Forms</label>
                <div className="mt-1 space-y-1.5 max-h-40 overflow-y-auto border border-curtron-line rounded-md p-2">
                  {forms.map((f) => (
                    <label key={f.key} className="flex items-center gap-2 text-sm text-curtron-ink cursor-pointer">
                      <input type="checkbox" checked={form.required_forms.includes(f.key)} onChange={() => toggleForm(f.key)} className="w-4 h-4 accent-[#2C4C3B]" /> {f.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn onClick={save} disabled={saving} data-testid="agency-save">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="agency-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.name}</DialogTitle>
            <DialogDescription>{[detail?.address, [detail?.city, detail?.state, detail?.zip].filter(Boolean).join(", ")].filter(Boolean).join(", ")}</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <a href={mapsDir(detail?.address, detail?.city, detail?.state, detail?.zip)} target="_blank" rel="noopener noreferrer" className="flex-1"><Btn variant="secondary" className="w-full"><Navigation className="w-4 h-4" /> Directions</Btn></a>
            <Btn className="flex-1" onClick={() => navigate(`/transports/new`)} data-testid="agency-new-transport"><Truck className="w-4 h-4" /> New transport</Btn>
          </div>
          {stats === null ? <Loading /> : (
            <>
              <div className="grid grid-cols-3 gap-2">
                <StatBox label="This month" value={stats.totals?.this_month ?? 0} />
                <StatBox label="Year to date" value={stats.totals?.ytd ?? 0} />
                <StatBox label="All time" value={stats.totals?.all_time ?? 0} />
                <StatBox label="Active" value={stats.totals?.active ?? 0} />
                <StatBox label="Avg response" value={stats.totals?.avg_response_min != null ? `${stats.totals.avg_response_min} min` : "—"} />
                <StatBox label="Total miles" value={stats.totals?.total_miles ?? "—"} />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-curtron-ink text-sm mb-2">Recent transports</h3>
                {(stats.recent || []).length === 0 ? (
                  <p className="text-sm text-curtron-sub">No transports for this agency yet.</p>
                ) : (
                  <div className="divide-y divide-curtron-line border border-curtron-line rounded-md">
                    {stats.recent.slice(0, 8).map((r) => (
                      <button key={r.id} onClick={() => { setDetail(null); navigate(`/transports/${r.id}`); }} className="w-full text-left px-3 py-2 text-sm hover:bg-curtron-bg flex justify-between gap-2">
                        <span className="truncate text-curtron-ink">{r.deceased_name || "—"}</span>
                        <span className="text-xs text-curtron-muted shrink-0">{r.status}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
